import type { ComponentType } from 'react';
import { AvailableCountries, type IUser } from 'types/users.types';
import type { MonthlyPaymentVolume } from 'types/settings.types';
import type { ProgressSegment } from '../components/SmartOnboardingProgressBar';
// Constants - imported from constants files to avoid circular deps
import {
  PostAccountStepId,
  type BackendActivationStepIdType,
} from '../ActivationFlow/constants/stepIds';
import { StepComponentName } from '../ActivationFlow/constants/componentNames';

// Analytics events
import {
  WEBAPP_BANKING_ONBOARDING_VISITEDMEETACCOUNTABLEBAKINGSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEDAUTOMATETAXESSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEOURPROMISESSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEPERSONALDETAILSFORMSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEPROOFOFREGISTRATIONSCREEN,
  WEBAPP_BANKING_ONBOARDING_VISITEPIDENTITYCONFIRMATIONSCREEN,
} from 'utils/amplitude/events/bank.amplitude';

// Icons
import NewBankIcon from 'assets/svg/icons/newBankIcon';
import PlugOnIcon from 'assets/svg/icons/plugOnIcon';
import ConfettiPartyIcon from 'assets/svg/icons/confettiPartyIcon';
import { UserIcon } from 'utils/icons/figmaIcons';

// ===========================
// ENUMS & TYPE DEFINITIONS
// ===========================

/**
 * Step mode enum - determines whether step is before or after account creation
 */
export enum StepMode {
  PreAccount = 'pre_account',
  PostAccount = 'post_account',
}

export type StepStatus = 'pending' | 'in_progress' | 'completed' | 'skipped';

/**
 * Main step grouping - represents a high-level step in the flow
 * Can contain multiple substeps
 */
export interface MainStepDefinition {
  id: string;
  label: string; // For progress list display
  listIcon: ComponentType<{ className?: string }>;
  substeps: SubstepDefinition[];
}

/**
 * Substep - individual screen/component within a main step
 */
export interface SubstepDefinition {
  id: string;
  mode: StepMode;
  backendStep?: BackendActivationStepIdType; // For post-account steps with backend tracking

  // Component to render (referenced by name to avoid circular dependencies)
  componentName: StepComponentName;

  // Props factory - generates props for the component
  // Receives flow state and handlers
  renderProps: (context: StepRenderContext) => any;

  // Metadata
  titleKey?: string; // Translation key for modal title
  subtitleKey?: string; // Translation key for modal subtitle
  modalIcon?: ComponentType<{ className?: string }>; // Optional icon for this specific substep
  analyticsEvent?: string; // Amplitude event to log

  // Flags
  isBookmark?: boolean; // Whether this is a major milestone
  requiresAuth?: boolean; // Whether user must be authenticated
  countrySpecific?: AvailableCountries[];
}

/**
 * Context passed to renderProps factory
 * Contains all state and handlers needed by step components
 */
export interface StepRenderContext {
  // Navigation handlers
  onContinue: () => void;
  onBack: () => void;
  onSkip?: (targetSubstepId: string) => void; // Skip to a specific substep

  // Progress information (for smart progress bar)
  progressSegments?: ProgressSegment[];
  onProgressSegmentClick?: (segmentId: string) => void;
  currentStepNumber?: number;
  totalSteps?: number;

  // Step metadata (automatically injected by system)
  title?: string; // Translated titleKey
  subtitle?: string; // Translated subtitleKey
  icon?: ComponentType<{ className?: string }>; // From substep's modalIcon

  // Loading states
  isUpdatingStep?: boolean; // True when backend step update is in progress

  // Pre-account state
  onboardingId: string;
  setOnboardingId: (id: string) => void;
  accountDetails?: IUser & {
    monthlyPaymentVolume: MonthlyPaymentVolume;
    companyName: string;
    additionalBusinessActivityDescription: string;
  };
  setAccountDetails: (details: any) => void;
  proofOfRegistrationFiles: { url: string; payload: string }[];
  setProofOfRegistrationFiles: React.Dispatch<
    React.SetStateAction<{ url: string; payload: string }[]>
  >;

  // API mutations
  onboardingAPI: any;
  createBankConnection: any;
  isCreatingConnection: boolean;

  // User data
  user?: IUser;
  t: (key: string, options?: any) => string;
}

// ===========================
// MAIN STEP DEFINITIONS
// ===========================

/**
 * Complete step hierarchy for Accountable Banking flow
 * Organized by main steps with substeps
 */
export const MAIN_STEPS: MainStepDefinition[] = [
  // ==========================================
  // PRE-ACCOUNT: STEP 1 - Sign up with Accountable
  // ==========================================
  {
    id: 'signup_intro',
    label: 'accountable_banking.activation.init_activation.step_1',
    listIcon: NewBankIcon,
    substeps: [
      {
        id: 'meet_accountable_banking',
        mode: StepMode.PreAccount,
        componentName: StepComponentName.MeetAccountableBanking,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEDMEETACCOUNTABLEBAKINGSCREEN,
        renderProps: (ctx) => ({
          onConfirm: ctx.onContinue,
        }),
      },
      {
        id: 'automatically_reserve_taxes',
        mode: StepMode.PreAccount,
        componentName: StepComponentName.AutomaticallyReserveTaxes,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEDAUTOMATETAXESSCREEN,
        renderProps: (ctx) => ({
          onConfirm: ctx.onContinue,
        }),
      },
      {
        id: 'full_transparency',
        mode: StepMode.PreAccount,
        componentName: StepComponentName.FullTransparency,
        analyticsEvent: WEBAPP_BANKING_ONBOARDING_VISITEOURPROMISESSCREEN,
        renderProps: (ctx) => ({
          onConfirm: ctx.onContinue,
        }),
      },
    ],
  },

  // ==========================================
  // PRE-ACCOUNT: STEP 2 - Provide personal details
  // ==========================================
  {
    id: 'personal_details',
    label: 'accountable_banking.activation.init_activation.step_2',
    listIcon: UserIcon,
    substeps: [
      {
        id: 'personal_details_form',
        mode: StepMode.PreAccount,
        componentName: StepComponentName.OnboardingForm,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEPERSONALDETAILSFORMSCREEN,
        renderProps: (ctx) => ({
          isConfirming: ctx.onboardingAPI.isLoading,
          currentAccountDetailsValues: ctx.accountDetails,
          onConfirm: (values: any) => {
            ctx.setAccountDetails(values);

            // Belgium: skip backend onboarding for now
            if (ctx.user?.country === AvailableCountries.belgium) {
              return ctx.onContinue();
            }

            // Other countries: initiate backend onboarding
            return ctx.onboardingAPI.mutate(
              {
                userId: ctx.user?._id,
                country: ctx.user?.country,
                taxID: values?.steuernummer,
                personalDetails: {
                  firstName: values?.firstName,
                  lastName: values?.lastName,
                  email: values?.email,
                  address: {
                    city: values?.address?.city,
                    street: values?.address?.street,
                    zip: values?.address?.zip,
                    state: values?.address?.state,
                  },
                },
                businessActivity: values?.APC1,
                businessActivityDescription: ctx.t(
                  `${ctx.user?.country}.APC2.${values?.APC2}`,
                ),
                monthlyPaymentVolume: values?.monthlyPaymentVolume,
                companyName: values?.companyName ?? undefined,
                additionalBusinessActivityDescription:
                  values?.additionalBusinessActivityDescription,
              },
              {
                onSuccess: (data: any) => {
                  ctx.setOnboardingId(data._id);
                  ctx.onContinue();
                },
              },
            );
          },
        }),
      },
    ],
  },

  // ==========================================
  // PRE-ACCOUNT: STEP 3 - Upload proof of registration
  // ==========================================
  {
    id: 'proof_of_registration',
    label: 'accountable_banking.activation.init_activation.step_3',
    listIcon: PlugOnIcon, // TODO: Replace with appropriate icon
    substeps: [
      {
        id: 'proof_of_registration_uploader',
        mode: StepMode.PreAccount,
        componentName: StepComponentName.FinanzamtDocUploader,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEPROOFOFREGISTRATIONSCREEN,
        countrySpecific: [AvailableCountries.germany],
        renderProps: (ctx) => ({
          onConfirm: ctx.onContinue,
          defaultFiles: ctx.proofOfRegistrationFiles,
          files: ctx.proofOfRegistrationFiles,
          setFiles: ctx.setProofOfRegistrationFiles,
          onboardingId: ctx.onboardingId,
          isConfirming: ctx.isCreatingConnection || ctx.onboardingAPI.isLoading,
          onUploaded: (file: { url: string; payload: string }) => {
            ctx.setProofOfRegistrationFiles(
              (files: { url: string; payload: string }[]) => [...files, file],
            );
          },
        }),
      },
    ],
  },

  // ==========================================
  // PRE-ACCOUNT: STEP 4 - Verify your identity
  // ==========================================
  {
    id: 'verify_identity',
    label: 'accountable_banking.activation.init_activation.step_4',
    listIcon: PlugOnIcon, // TODO: Replace with appropriate icon
    substeps: [
      {
        id: 'confirm_identity',
        mode: StepMode.PreAccount,
        componentName: StepComponentName.ConfirmIdentity,
        analyticsEvent:
          WEBAPP_BANKING_ONBOARDING_VISITEPIDENTITYCONFIRMATIONSCREEN,
        renderProps: (ctx) => ({
          isConfirming: ctx.isCreatingConnection || ctx.onboardingAPI.isLoading,
          onConfirm: () => {
            ctx.createBankConnection(
              { provider: 'swan' },
              {
                onSuccess: (data: any) => {
                  // Belgium: create onboarding after bank connection
                  if (ctx.user?.country === AvailableCountries.belgium) {
                    ctx.onboardingAPI.mutate(
                      {
                        userId: ctx.user?._id,
                        country: ctx.user?.country,
                        taxID: ctx.accountDetails?.VATNumber,
                        personalDetails: {
                          firstName: ctx.accountDetails?.firstName,
                          lastName: ctx.accountDetails?.lastName,
                          email: ctx.accountDetails?.email,
                          address: {
                            city: ctx.accountDetails?.address?.city,
                            street: ctx.accountDetails?.address?.street,
                            zip: ctx.accountDetails?.address?.zip,
                          },
                        },
                        businessActivity: ctx.accountDetails?.APC1,
                        businessActivityDescription: ctx.t(
                          `${ctx.user?.country}.APC2.${ctx.accountDetails?.APC2}`,
                        ),
                        monthlyPaymentVolume:
                          ctx.accountDetails?.monthlyPaymentVolume,
                        companyName:
                          ctx.accountDetails?.companyName ?? undefined,
                        additionalBusinessActivityDescription:
                          ctx.accountDetails
                            ?.additionalBusinessActivityDescription,
                      },
                      {
                        onSuccess: () => {
                          window.location.href = data.redirectUri;
                        },
                      },
                    );
                  } else {
                    // Germany/Other countries: Update onboarding with filepaths before redirect
                    ctx.onboardingAPI.mutate(
                      {
                        _id: ctx.onboardingId,
                        userId: ctx.user?._id,
                        country: ctx.user?.country,
                        taxID: ctx.accountDetails?.steuernummer,
                        filepaths: ctx.proofOfRegistrationFiles?.map(
                          (file) => file.url,
                        ),
                        personalDetails: {
                          firstName: ctx.accountDetails?.firstName,
                          lastName: ctx.accountDetails?.lastName,
                          email: ctx.accountDetails?.email,
                          address: {
                            city: ctx.accountDetails?.address?.city,
                            street: ctx.accountDetails?.address?.street,
                            zip: ctx.accountDetails?.address?.zip,
                            state: ctx.accountDetails?.address?.state,
                          },
                        },
                        businessActivity: ctx.accountDetails?.APC1,
                        businessActivityDescription: ctx.t(
                          `${ctx.user?.country}.APC2.${ctx.accountDetails?.APC2}`,
                        ),
                      },
                      {
                        onSuccess: () => {
                          window.location.href = data.redirectUri;
                        },
                      },
                    );
                  }
                },
              },
            );
          },
        }),
      },
    ],
  },

  // ==========================================
  // PRE-ACCOUNT: STEP 5 - Send the first transfer
  // ==========================================
  {
    id: 'first_transfer',
    label: 'accountable_banking.activation.init_activation.step_5',
    listIcon: PlugOnIcon, // TODO: Replace with appropriate icon
    substeps: [
      // Placeholder substep - will be implemented later
      // For now, component shows brief message and auto-continues
      {
        id: 'first_transfer',
        mode: StepMode.PreAccount,
        componentName: StepComponentName.ReadyToGoStep, // Temporary placeholder
        titleKey: 'accountable_banking.activation.first_transfer.title',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
        }),
      },
    ],
  },

  // ==========================================
  // POST-ACCOUNT: STEP 1 - Embed banking in your Accountable
  // ==========================================
  {
    id: 'embed_banking',
    label: 'accountable_banking.activation.init_activation.step_6',
    listIcon: PlugOnIcon,
    substeps: [
      {
        id: PostAccountStepId.InitActivation,
        mode: StepMode.PostAccount,
        backendStep: PostAccountStepId.InitActivation,
        componentName: StepComponentName.InitActivationStep,
        titleKey: 'accountable_banking.activation.init_activation.title',
        modalIcon: NewBankIcon,
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          showProgressBar: false,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.HowIsIntegrated,
        mode: StepMode.PostAccount,
        backendStep: PostAccountStepId.HowIsIntegrated,
        componentName: StepComponentName.HowIsIntegratedStep,
        titleKey: 'accountable_banking.activation.how_is_integrated.title',
        subtitleKey:
          'accountable_banking.activation.how_is_integrated.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          onBack: ctx.onBack,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.TaxesDescription,
        mode: StepMode.PostAccount,
        backendStep: PostAccountStepId.TaxesDescription,
        componentName: StepComponentName.TaxesDescriptionStep,
        titleKey: 'accountable_banking.activation.taxes_description.title',
        subtitleKey:
          'accountable_banking.activation.taxes_description.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          onBack: ctx.onBack,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.AccIbanOnInvoices,
        mode: StepMode.PostAccount,
        backendStep: PostAccountStepId.AccIbanOnInvoices,
        componentName: StepComponentName.AccIbanOnInvoicesStep,
        isBookmark: true,
        titleKey: 'accountable_banking.activation.acc_iban_on_invoices.title',
        subtitleKey:
          'accountable_banking.activation.acc_iban_on_invoices.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          onBack: ctx.onBack,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.IssueCard,
        mode: StepMode.PostAccount,
        backendStep: PostAccountStepId.IssueCard,
        componentName: StepComponentName.IssueCardStep,
        isBookmark: true,
        titleKey: 'accountable_banking.activation.issue_card.title',
        subtitleKey: 'accountable_banking.activation.issue_card.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          onBack: ctx.onBack,
          // Skip to YourCardReady when user has existing card (component checks this internally)
          onSkipToCardReady: ctx.onSkip
            ? () => ctx.onSkip?.(PostAccountStepId.YourCardReady)
            : undefined,
          // Skip to TopUp when user clicks "I don't need a card"
          onSkip: ctx.onSkip
            ? () => ctx.onSkip?.(PostAccountStepId.TopUp)
            : undefined,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.CardDesign,
        mode: StepMode.PostAccount,
        componentName: StepComponentName.CardDesignStep,
        titleKey: 'accountable_banking.activation.issue_card.customize_title',
        subtitleKey:
          'accountable_banking.activation.issue_card.customize_subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          onBack: ctx.onBack,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.YourCardReady,
        mode: StepMode.PostAccount,
        componentName: StepComponentName.YourCardReadyStep,
        titleKey: 'accountable_banking.activation.card_to_wallet.ready_title',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          onBack: ctx.onBack,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.CardToWallet,
        mode: StepMode.PostAccount,
        backendStep: PostAccountStepId.CardToWallet,
        componentName: StepComponentName.CardToWalletStep,
        // Note: Title managed internally by component (switches between Apple Pay/Google Pay)
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          onBack: ctx.onBack,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.TopUp,
        mode: StepMode.PostAccount,
        backendStep: PostAccountStepId.TopUp,
        componentName: StepComponentName.TopUpStep,
        isBookmark: true,
        titleKey: 'accountable_banking.activation.top_up.title',
        subtitleKey: 'accountable_banking.activation.top_up.subtitle',
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          ...getWrapperProps(ctx),
        }),
      },
      {
        id: PostAccountStepId.ReadyToGo,
        mode: StepMode.PostAccount,
        backendStep: PostAccountStepId.ReadyToGo,
        componentName: StepComponentName.ReadyToGoStep,
        titleKey: 'accountable_banking.activation.ready_to_go.title',
        modalIcon: ConfettiPartyIcon,
        renderProps: (ctx) => ({
          onContinue: ctx.onContinue,
          showProgressBar: false,
          ...getWrapperProps(ctx),
        }),
      },
    ],
  },
];

// ===========================
// HELPER FUNCTIONS & EXPORTS
// ===========================

/**
 * Helper to extract wrapper props from context
 * Includes progress bar, title, subtitle, icon, etc.
 * Use this in renderProps to automatically get all wrapper-related props
 */
export const getWrapperProps = (ctx: StepRenderContext) => ({
  // Progress bar props
  // Note: showProgressBar defaults to true in OnboardingStepWrapper
  // Steps can explicitly pass showProgressBar: false to hide it
  progressSegments: ctx.progressSegments,
  onProgressSegmentClick: ctx.onProgressSegmentClick,
  // Metadata props (automatically translated and injected)
  title: ctx.title,
  subtitle: ctx.subtitle,
  icon: ctx.icon,
  isUpdatingStep: ctx.isUpdatingStep,
});

/**
 * Flatten main steps into a single array of substeps
 * This is what UnifiedActivationFlow will iterate through
 */
export const getAllSubsteps = (): SubstepDefinition[] => {
  return MAIN_STEPS.flatMap((mainStep) => mainStep.substeps);
};

/**
 * Get all post-account backend steps (for progress calculation)
 */
export const getPostAccountBackendSteps = (): BackendActivationStepIdType[] => {
  return getAllSubsteps()
    .filter(
      (substep) => substep.mode === StepMode.PostAccount && substep.backendStep,
    )
    .map((substep) => substep.backendStep!);
};

/**
 * Find substep by ID
 */
export const findSubstepById = (id: string): SubstepDefinition | undefined => {
  return getAllSubsteps().find((substep) => substep.id === id);
};

/**
 * Find substep by backend step
 */
export const findSubstepByBackendStep = (
  backendStep: BackendActivationStepIdType,
): SubstepDefinition | undefined => {
  return getAllSubsteps().find(
    (substep) => substep.backendStep === backendStep,
  );
};

/**
 * Get main step that contains a given substep
 */
export const getMainStepForSubstep = (
  substepId: string,
): MainStepDefinition | undefined => {
  return MAIN_STEPS.find((mainStep) =>
    mainStep.substeps.some((substep) => substep.id === substepId),
  );
};

/**
 * Calculate progress percentage
 * Pre-account = 0% → 75%, Post-account = 75% → 100%
 */
export const ACTIVATION_PROGRESS_START_PERCENTAGE = 75;

export const calculateActivationProgress = (
  furthestStep: BackendActivationStepIdType | null | undefined,
): number => {
  if (!furthestStep) {
    return ACTIVATION_PROGRESS_START_PERCENTAGE;
  }

  const postAccountSteps = getPostAccountBackendSteps();
  const currentIndex = postAccountSteps.indexOf(furthestStep);

  if (currentIndex === -1) {
    return ACTIVATION_PROGRESS_START_PERCENTAGE;
  }

  const remainingPercentage = 100 - ACTIVATION_PROGRESS_START_PERCENTAGE;
  const stepProgress =
    ((currentIndex + 1) / postAccountSteps.length) * remainingPercentage;

  return Math.round(ACTIVATION_PROGRESS_START_PERCENTAGE + stepProgress);
};
